import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Search, X, StickyNote, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { api, uploadUrl, formatApiErrorDetail, BACKEND_URL } from "../lib/api";
import { statusColor } from "./AdminDashboard";

const STATUSES = [
  "Nuova richiesta",
  "Da contattare",
  "Intervento programmato",
  "Preventivo in preparazione",
  "Preventivo inviato",
  "Acquisita",
  "Non acquisita",
];

export default function AdminLeads() {
  const [leads, setLeads] = useState(null);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const status = searchParams.get("status") || "";

  const load = useCallback(() => {
    const params = {};
    if (status) params.status = status;
    if (q) params.q = q;
    api.get("/admin/leads", { params }).then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, [status, q]);

  useEffect(() => {
    setLeads(null);
    load();
  }, [load]);

  useEffect(() => {
    const id = searchParams.get("id");
    if (id) {
      api.get(`/admin/leads/${id}`).then((r) => setSelected(r.data)).catch(() => {});
    }
  }, [searchParams]);

  const patch = async (id, body) => {
    try {
      const r = await api.patch(`/admin/leads/${id}`, body);
      setSelected(r.data);
      setLeads((ls) => ls.map((l) => (l.id === id ? r.data : l)));
      if (body.note) {
        setNote("");
        toast.success("Nota aggiunta");
      }
      if (body.status) toast.success("Stato aggiornato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "richieste-v32.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Esportazione non riuscita");
    }
  };

  return (
    <div data-testid="admin-leads">
      <Seo title="Richieste | V32 Admin" noindex />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display font-light text-3xl text-ink">Richieste</h1>
        <button
          onClick={exportCsv}
          data-testid="export-csv-button"
          className="inline-flex items-center gap-2 bg-antracite text-avorio text-sm font-medium px-5 py-2.5 hover:bg-bronzo transition-colors duration-300"
        >
          <Download className="w-4 h-4" /> Esporta CSV
        </button>
      </div>

      <div className="mt-8 flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-ink/40" />
          <input
            data-testid="leads-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per nome, email o comune…"
            className="w-full bg-white border border-ink/15 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-bronzo transition-colors duration-300"
          />
        </div>
        <select
          data-testid="leads-status-filter"
          value={status}
          onChange={(e) => setSearchParams(e.target.value ? { status: e.target.value } : {})}
          className="bg-white border border-ink/15 px-4 py-2.5 text-sm focus:outline-none focus:border-bronzo"
        >
          <option value="">Tutti gli stati</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div className="mt-6">
        {leads === null ? (
          <div data-testid="leads-loading" />
        ) : leads.length === 0 ? (
          <p className="bg-white border border-ink/10 p-8 text-sm text-ink/50" data-testid="leads-empty">
            Nessuna richiesta trovata.
          </p>
        ) : (
          <div className="bg-white border border-ink/10 overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]" data-testid="leads-table">
              <thead>
                <tr className="border-b border-ink/10 text-left">
                  <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50 font-medium">Cliente</th>
                  <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50 font-medium">Intervento</th>
                  <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50 font-medium">Comune</th>
                  <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50 font-medium">Stato</th>
                  <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50 font-medium">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/5">
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    data-testid={`lead-row-${l.id}`}
                    onClick={() => setSelected(l)}
                    className="hover:bg-beige/50 cursor-pointer transition-colors duration-200"
                  >
                    <td className="px-5 py-4">
                      <p className="font-medium text-ink">{l.name}</p>
                      <p className="text-xs text-ink/50">{l.email}</p>
                    </td>
                    <td className="px-5 py-4 text-ink/70">{l.project_type}</td>
                    <td className="px-5 py-4 text-ink/70">{l.comune}</td>
                    <td className="px-5 py-4">
                      <span className={`text-[11px] font-medium px-2.5 py-1 ${statusColor(l.status)}`}>{l.status}</span>
                    </td>
                    <td className="px-5 py-4 text-ink/40 font-mono text-xs">
                      {new Date(l.created_at).toLocaleDateString("it-CH")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-drawer">
          <div className="absolute inset-0 bg-antracite/40" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-lg bg-avorio h-full overflow-y-auto p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display font-medium text-2xl text-ink">{selected.name}</h2>
                <p className="mt-1 font-mono text-xs text-ink/40">
                  {new Date(selected.created_at).toLocaleString("it-CH")} · {selected.source}
                </p>
              </div>
              <button onClick={() => setSelected(null)} data-testid="lead-drawer-close" aria-label="Chiudi dettaglio" className="p-2 text-ink/50 hover:text-bronzo">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <a href={`tel:${selected.phone.replace(/\s/g, "")}`} data-testid="lead-call" className="inline-flex items-center gap-2 bg-antracite text-avorio text-sm px-4 py-2 hover:bg-bronzo transition-colors duration-300">
                <Phone className="w-4 h-4" /> {selected.phone}
              </a>
              <a href={`mailto:${selected.email}`} data-testid="lead-email" className="inline-flex items-center gap-2 border border-ink/20 text-ink text-sm px-4 py-2 hover:border-bronzo hover:text-bronzo transition-colors duration-300">
                <Mail className="w-4 h-4" /> Email
              </a>
            </div>

            <div className="mt-8">
              <label htmlFor="lead-status" className="block font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50 mb-2">
                Stato della richiesta
              </label>
              <select
                id="lead-status"
                data-testid="lead-status-select"
                value={selected.status}
                onChange={(e) => patch(selected.id, { status: e.target.value })}
                className="w-full bg-white border border-ink/15 px-4 py-2.5 text-sm focus:outline-none focus:border-bronzo"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <dl className="mt-8 space-y-4 text-sm">
              {[
                ["Servizio", selected.project_type],
                ["Tipologia cliente", selected.client_type],
                ["Comune", `${selected.comune} ${selected.cap || ""}`.trim()],
                ["Indirizzo intervento", selected.address],
                ["Capacità boiler", selected.boiler_liters],
                ["Marca/modello boiler", selected.boiler_model],
                ["Numero circuiti", selected.circuits],
                ["Filtro marca/modello", selected.filter_model],
                ["Problemi riscontrati", selected.problems],
                ["Prezzo indicativo", selected.price_estimate],
                ["Preferenza contatto", selected.contact_preference],
                ["Ultimo aggiornamento", new Date(selected.updated_at).toLocaleString("it-CH")],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-4">
                  <dt className="w-36 shrink-0 font-mono text-[10px] uppercase tracking-[0.15em] text-ink/50 pt-0.5">{k}</dt>
                  <dd className="text-ink/80">{v || "—"}</dd>
                </div>
              ))}
              {selected.description && (
                <div className="flex gap-4">
                  <dt className="w-36 shrink-0 font-mono text-[10px] uppercase tracking-[0.15em] text-ink/50 pt-0.5">Note</dt>
                  <dd className="text-ink/80 whitespace-pre-line">{selected.description}</dd>
                </div>
              )}
            </dl>

            {selected.photos?.length > 0 && (
              <div className="mt-8" data-testid="lead-photos">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50 mb-3">
                  Fotografie allegate ({selected.photos.length})
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {selected.photos.map((p) => (
                    <a key={p} href={uploadUrl(p)} target="_blank" rel="noopener noreferrer">
                      <img src={uploadUrl(p)} alt="Foto bagno esistente allegata dal cliente" className="w-full aspect-square object-cover border border-ink/10" loading="lazy" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50 mb-3">Note interne</p>
              <div className="space-y-3">
                {(selected.notes || []).map((n, i) => (
                  <div key={i} className="bg-beige/60 border border-ink/10 p-3.5 text-sm text-ink/75">
                    <p>{n.text}</p>
                    <p className="mt-1.5 text-[10px] font-mono text-ink/40">{new Date(n.at).toLocaleString("it-CH")}</p>
                  </div>
                ))}
                {(selected.notes || []).length === 0 && (
                  <p className="text-xs text-ink/40">Nessuna nota ancora.</p>
                )}
              </div>
              <div className="mt-4 flex gap-2">
                <input
                  data-testid="lead-note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Aggiungi una nota…"
                  className="flex-1 bg-white border border-ink/15 px-4 py-2.5 text-sm focus:outline-none focus:border-bronzo"
                  onKeyDown={(e) => e.key === "Enter" && note.trim() && patch(selected.id, { note })}
                />
                <button
                  data-testid="lead-note-add"
                  onClick={() => note.trim() && patch(selected.id, { note })}
                  className="inline-flex items-center gap-2 bg-antracite text-avorio text-sm px-4 py-2.5 hover:bg-bronzo transition-colors duration-300"
                >
                  <StickyNote className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
